import type { CmsBettingVendor, CmsBettingVendorStatus } from '@/types/cms/bettingVendor'

type StrapiImageAsset = {
    url?: string
    attributes?: {
        url?: string
    }
}

type StrapiImage = {
    data?: StrapiImageAsset | StrapiImageAsset[] | null
}

type VendorShape = {
    name?: string
    DisplayOrder?: number
    image?: StrapiImage
    imageSUT?: StrapiImage
    imageSquare?: StrapiImage
}

type NestedAgent = {
    id: number
    attributes?: {
        slug?: string
        status?: string
        walletId?: number | null
        vendor?: {
            data?: {
                id: number
                attributes?: VendorShape
            } | null
        } | null
    }
}

type FlatAgent = {
    id: number
    slug?: string
    status?: string
    walletId?: number | null
    vendor?: VendorShape | null
}

type AgentsResponse = {
    data?: NestedAgent[] | FlatAgent[]
    agents?: FlatAgent[]
}

const CMS_BASE_URL = import.meta.env.VITE_STRAPI_RS || ''
const AGENTS_ENDPOINT = '/api/agents'

const buildImageUrl = (path?: string | null) => {
    if (!path) return null
    if (path.startsWith('http')) return path
    if (!CMS_BASE_URL) return path
    return `${CMS_BASE_URL}${path}`
}

const getAssetUrl = (asset?: StrapiImageAsset) => {
    return asset?.attributes?.url ?? asset?.url
}

const extractImageUrl = (image?: StrapiImage) => {
    if (!image?.data) return null
    if (Array.isArray(image.data)) {
        return buildImageUrl(getAssetUrl(image.data[0]))
    }
    return buildImageUrl(getAssetUrl(image.data))
}

const mapStatus = (status?: string): CmsBettingVendorStatus => {
    if (status === 'coming-soon') return 'coming-soon'
    if (status === 'available') return 'available'
    return 'unavailable'
}

const buildAgentsUrl = () => {
    const url = new URL(AGENTS_ENDPOINT, CMS_BASE_URL)
    const params = new URLSearchParams()

    params.append('fields[0]', 'slug')
    params.append('fields[1]', 'status')
    params.append('fields[2]', 'walletId')
    params.append('populate[vendor][fields][0]', 'name')
    params.append('populate[vendor][fields][1]', 'DisplayOrder')
    params.append('populate[vendor][populate][image]', '*')
    params.append('populate[vendor][populate][imageSUT]', '*')
    params.append('populate[vendor][populate][imageSquare]', '*')
    params.append('filters[vendor][id][$notNull]', 'true')

    url.search = params.toString()
    return url.toString()
}

const mapAgentToVendor = (agent: FlatAgent): CmsBettingVendor | null => {
    const vendor = agent.vendor
    const vendorName = vendor?.name?.trim()
    const slug = agent.slug?.trim()

    if (!vendor || !vendorName || !slug) return null

    const sortOrder = vendor.DisplayOrder ?? Number.MAX_SAFE_INTEGER

    return {
        id: agent.id,
        slug,
        name: vendorName,
        walletId: agent.walletId ?? null,
        status: mapStatus(agent.status),
        displayOrder: sortOrder,
        sutImageUrl: extractImageUrl(vendor.imageSUT),
        wideImageUrl: extractImageUrl(vendor.image),
        squareImageUrl: extractImageUrl(vendor.imageSquare)
    }
}

const normalizeAgents = (response: AgentsResponse): FlatAgent[] => {
    if (Array.isArray(response.agents)) {
        return response.agents
    }

    if (!Array.isArray(response.data)) {
        return []
    }

    return response.data.map((item) => {
        const maybeNested = item as NestedAgent

        if (maybeNested.attributes) {
            return {
                id: maybeNested.id,
                slug: maybeNested.attributes.slug,
                status: maybeNested.attributes.status,
                walletId: maybeNested.attributes.walletId,
                vendor: maybeNested.attributes.vendor?.data?.attributes ?? null
            }
        }

        return item as FlatAgent
    })
}

const getBettingVendors = async (): Promise<CmsBettingVendor[]> => {
    if (!CMS_BASE_URL) {
        throw new Error('CMS base URL is not configured')
    }

    const response = await fetch(buildAgentsUrl())
    if (!response.ok) {
        throw new Error('Failed to fetch betting vendors')
    }

    const data = (await response.json()) as AgentsResponse
    const agents = normalizeAgents(data)

    return agents
        .map(mapAgentToVendor)
        .filter((vendor): vendor is CmsBettingVendor => vendor !== null)
        .filter((vendor) =>
            vendor.status === 'available' || vendor.status === 'coming-soon'
        )
        .sort((a, b) => a.displayOrder - b.displayOrder)
}

export const cmsBettingVendorService = {
    getBettingVendors
}
